/**
 * Redux Selectors for Search Feature
 *
 * Flattens and enriches projects/pairs data for search functionality.
 * Uses Reselect for memoization to prevent unnecessary recalculations.
 */

import { createSelector } from '@reduxjs/toolkit';

import { ICexPairShort } from 'types/pairs';
import { IProject } from 'types/project';
import { ENetwork } from 'web3';

import type { RootStore } from '../../../store';

/**
 * Enriched pair data for search results
 */
interface EnrichedPair {
  id: number;
  pairId: number;
  projectId: number;
  projectName: string;
  symbol: string;
  baseToken: string;
  quoteToken: string;
  pairType: 'cex' | 'dex' | 'sol';
  platform: string;
  network?: ENetwork;
  isArchived: boolean;
}

/**
 * Parse symbol into base and quote tokens
 * "BTC/USDT" -> { baseToken: "BTC", quoteToken: "USDT" }
 */
const parseTokens = (symbol: string): { baseToken: string; quoteToken: string } => {
  const [base, quote] = symbol.split('/');
  return {
    baseToken: base?.trim() ?? '',
    quoteToken: quote?.trim() ?? '',
  };
};

/**
 * Base selector: Get active projects from Redux store
 */
export const selectProjects = (state: RootStore): IProject[] | null => state.projects.projects;

/**
 * Helper: shape project data for search consumers
 */
const formatProjectForSearch = (project: IProject, status: 'active' | 'archived') => ({
  projectId: project.id,
  name: project.name,
  pairsCount: project.cexPairs.length + project.dexPairs.length + project.solPairs.length,
  status,
  created_at: project.created_at,
});

/**
 * Selector: Get all projects formatted for search
 * Returns projects with computed pairsCount and status for display
 */
export const selectProjectsForSearch = createSelector(
  [selectProjects, (state: RootStore) => state.projects.archivedProjects],
  (activeProjects, archivedProjects) => {
    const active = activeProjects ?? [];
    const archived = archivedProjects ?? [];

    return [
      ...active.map(project => formatProjectForSearch(project, 'active')),
      ...archived.map(project => formatProjectForSearch(project, 'archived')),
    ];
  },
);

/**
 * Helper: Create enriched pairs from a project
 */
const enrichPairsFromProject = (project: IProject, isArchived: boolean): EnrichedPair[] => {
  const pairs: EnrichedPair[] = [];

  // CEX Pairs
  project.cexPairs.forEach(pair => {
    const tokens = parseTokens(pair.symbol);
    pairs.push({
      id: pair.id,
      pairId: pair.id,
      projectId: project.id,
      projectName: project.name,
      symbol: pair.symbol,
      ...tokens,
      pairType: 'cex',
      platform: pair.cex || 'unknown',
      isArchived,
    });
  });

  // DEX Pairs
  project.dexPairs.forEach(pair => {
    const tokens = parseTokens(pair.symbol);
    pairs.push({
      id: pair.id,
      pairId: pair.id,
      projectId: project.id,
      projectName: project.name,
      symbol: pair.symbol,
      ...tokens,
      pairType: 'dex',
      platform: pair.dex || 'unknown',
      network: pair.network as ENetwork,
      isArchived,
    });
  });

  // SOL Pairs
  project.solPairs.forEach(pair => {
    const tokens = parseTokens(pair.symbol);
    pairs.push({
      id: pair.id,
      pairId: pair.id,
      projectId: project.id,
      projectName: project.name,
      symbol: pair.symbol,
      ...tokens,
      pairType: 'sol',
      platform: 'raydium',
      network: ENetwork.solana,
      isArchived,
    });
  });

  return pairs;
};

/**
 * Selector: Get all pairs flattened and enriched for search
 * Combines CEX, DEX, and SOL pairs from all projects (active + archived) into a single searchable array
 */
export const selectAllPairsForSearch = createSelector(
  [selectProjects, (state: RootStore) => state.projects.archivedProjects],
  (activeProjects, archivedProjects) => {
    const allPairs: EnrichedPair[] = [];

    // Add active project pairs
    (activeProjects ?? []).forEach(project => {
      allPairs.push(...enrichPairsFromProject(project, false));
    });

    // Add archived project pairs
    (archivedProjects ?? []).forEach(project => {
      allPairs.push(...enrichPairsFromProject(project, true));
    });

    return allPairs;
  },
);

/**
 * Selector: Get pairs for a specific project
 * Used for project-scoped search
 */
export const selectPairsForProject = createSelector(
  [
    selectProjects,
    (state: RootStore) => state.projects.archivedProjects,
    (_state: RootStore, projectId: number) => projectId,
  ],
  (activeProjects, archivedProjects, projectId) => {
    // Check active projects first
    const activeProject = (activeProjects ?? []).find(p => p.id === projectId);
    if (activeProject) {
      return enrichPairsFromProject(activeProject, false);
    }

    // Check archived projects
    const archivedProject = (archivedProjects ?? []).find(p => p.id === projectId);
    if (archivedProject) {
      return enrichPairsFromProject(archivedProject, true);
    }

    return [];
  },
);

/**
 * Selector: Get CEX pairs by IDs from Redux store
 * Used by widgets to avoid duplicate API calls when data is already cached
 */
export const selectCexPairsByIds = createSelector(
  [
    selectProjects,
    (state: RootStore) => state.projects.archivedProjects,
    (_state: RootStore, pairIds: number[]) => pairIds,
  ],
  (activeProjects, archivedProjects, pairIds) => {
    if (pairIds.length === 0) return [];

    const idSet = new Set(pairIds);
    const result: ICexPairShort[] = [];

    // Check active projects
    (activeProjects ?? []).forEach(project => {
      project.cexPairs.forEach(pair => {
        if (idSet.has(pair.id)) {
          result.push(pair);
        }
      });
    });

    // Check archived projects
    (archivedProjects ?? []).forEach(project => {
      project.cexPairs.forEach(pair => {
        if (idSet.has(pair.id)) {
          result.push(pair);
        }
      });
    });

    return result;
  },
);

/**
 * Selector: Get single CEX pair by ID from Redux store
 * Used by widgets to avoid duplicate API calls when data is already cached
 */
export const selectCexPairById = createSelector(
  [
    selectProjects,
    (state: RootStore) => state.projects.archivedProjects,
    (_state: RootStore, pairId: number | undefined) => pairId,
  ],
  (activeProjects, archivedProjects, pairId) => {
    if (!pairId) return null;

    // Check active projects
    for (const project of activeProjects ?? []) {
      const pair = project.cexPairs.find(p => p.id === pairId);
      if (pair) return pair;
    }

    // Check archived projects
    for (const project of archivedProjects ?? []) {
      const pair = project.cexPairs.find(p => p.id === pairId);
      if (pair) return pair;
    }

    return null;
  },
);

/**
 * Export enriched pair type for use in other modules
 */
export type { EnrichedPair };
